type FailureSubtask = {
  hasErrors?: boolean
  metadata?: Record<string, unknown> | null
}

type FailureMessageTask = {
  stages: ReadonlyArray<{
    subtasks: ReadonlyArray<FailureSubtask>
  }>
}

/** Worker metadata code when a scratch reference download exceeds its deadline. */
export const TASK_FAILURE_REFERENCE_VIDEO_DOWNLOAD_TIMED_OUT =
  "PERSONA_REFERENCE_VIDEO_DOWNLOAD_TIMED_OUT"

/** Worker metadata code when the downloader rejects the post. */
export const TASK_FAILURE_REFERENCE_VIDEO_DOWNLOAD_FAILED =
  "PERSONA_REFERENCE_VIDEO_DOWNLOAD_FAILED"

/** Worker metadata code when Gemini cannot read the reference (missing or private). */
export const TASK_FAILURE_REFERENCE_VIDEO_UNAVAILABLE =
  "PERSONA_REFERENCE_VIDEO_UNAVAILABLE"

/** Worker metadata code when scratch reference analysis fails for any other reason. */
export const TASK_FAILURE_REFERENCE_VIDEO_ANALYSIS_FAILED =
  "PERSONA_REFERENCE_VIDEO_ANALYSIS_FAILED"

/** Shown when the card has a failure detail it does not know how to translate. */
export const TASK_FAILURE_UNKNOWN = "TASK_FAILURE_UNKNOWN"

const KNOWN_FAILURE_SENTENCES: Record<string, string> = {
  "The reference video took too long to download. Try again, or use a shorter video.":
    TASK_FAILURE_REFERENCE_VIDEO_DOWNLOAD_TIMED_OUT,
  "The reference video could not be downloaded. Check that the post is public, or generate without a video.":
    TASK_FAILURE_REFERENCE_VIDEO_DOWNLOAD_FAILED,
  "This video is missing or private. Try another link, or generate without a video.":
    TASK_FAILURE_REFERENCE_VIDEO_UNAVAILABLE,
  "The reference video could not be analyzed. Try again, or generate without a video.":
    TASK_FAILURE_REFERENCE_VIDEO_ANALYSIS_FAILED,
}

function readSubtaskFailureCode(subtask: FailureSubtask): string | null {
  const code = subtask.metadata?.failureCode
  if (typeof code === "string" && code.trim().length > 0) return code.trim()

  const message = subtask.metadata?.failureMessage
  if (typeof message !== "string") return null
  const trimmed = message.trim()
  if (trimmed.length === 0) return null
  return KNOWN_FAILURE_SENTENCES[trimmed] ?? TASK_FAILURE_UNKNOWN
}

/**
 * Stable failure code for the card. Errored subtasks come first, then the
 * rest, in stage order. A recognized code or mapped sentence wins. An
 * unrecognized sentence does not stop the search; the generic code is used
 * only when no subtask has anything more specific.
 */
export function readTaskFailureCode(task: FailureMessageTask): string | null {
  const subtasks = task.stages.flatMap((stage) => stage.subtasks)
  const ordered = [
    ...subtasks.filter((subtask) => subtask.hasErrors === true),
    ...subtasks.filter((subtask) => subtask.hasErrors !== true),
  ]
  let sawUnknown = false
  for (const subtask of ordered) {
    const code = readSubtaskFailureCode(subtask)
    if (!code) continue
    if (code === TASK_FAILURE_UNKNOWN) {
      sawUnknown = true
      continue
    }
    return code
  }
  return sawUnknown ? TASK_FAILURE_UNKNOWN : null
}
